import { useState, useEffect } from 'react'
import { Link } from 'react-router'
import { useAuthStore } from '@/stores/auth.store'
import { messagesApi } from '@/lib/messages-api'
import { ConversationItem } from '@/components/messages/ConversationItem'
import { SkeletonList } from '@/components/common'
import { useDeferredLoading } from '@/hooks/useDeferredLoading'
import type { Conversation } from '@/types/message'

interface SearchResult {
  id: string
  type: string
  name: string | null
  participants: Array<{ userId: string; name: string; role: string }>
  matchingMessage: { message: string; createdAt: string }
}

export function MessagesPage() {
  const { user } = useAuthStore()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const showLoading = useDeferredLoading(loading)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null)
  const [isSearching, setIsSearching] = useState(false)

  const fetchConversations = async () => {
    try {
      const response = await messagesApi.getConversations()
      setConversations(response.conversations)
      setError(null)
    } catch (err) {
      setError('Failed to load conversations')
      console.error('Error fetching conversations:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchConversations()

    // Poll for new messages every 30 seconds
    const interval = setInterval(fetchConversations, 30000)
    return () => clearInterval(interval)
  }, [])

  const handleSearch = async (query: string) => {
    setSearchQuery(query)

    if (!query.trim() || query.trim().length < 2) {
      setSearchResults(null)
      return
    }

    setIsSearching(true)
    try {
      const response = await messagesApi.search(query.trim())
      setSearchResults(response.conversations)
    } catch (err) {
      console.error('Search failed:', err)
    } finally {
      setIsSearching(false)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults(null)
  }

  if (!user) return null

  return (
    <div className="min-h-full bg-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-semibold text-gray-900">Messages</h1>
          <Link
            to="/messages/new"
            className="flex items-center gap-1 text-primary-600 font-medium"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            New
          </Link>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-10 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <svg
            className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {isSearching && (
            <svg className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-gray-400" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {searchQuery && !isSearching && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {searchResults !== null && (
          <p className="mt-2 text-sm text-gray-500">
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
          </p>
        )}
      </div>

      {/* Content */}
      {showLoading ? (
        <div className="p-4">
          <SkeletonList count={5} />
        </div>
      ) : loading ? (
        null
      ) : error ? (
        <div className="p-4">
          <div className="rounded-lg bg-red-50 p-4 text-center">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchConversations}
              className="mt-2 text-sm text-red-700 underline"
            >
              Try again
            </button>
          </div>
        </div>
      ) : conversations.length === 0 ? (
        <div className="p-8 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No messages yet</h3>
          <p className="text-gray-500 mb-4">
            {user.role === 'childminder'
              ? 'Start a conversation with a parent or send a broadcast message.'
              : 'Start a conversation with your childminder.'}
          </p>
          <Link
            to="/messages/new"
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white font-medium hover:bg-primary-700"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Start a conversation
          </Link>
        </div>
      ) : searchResults !== null ? (
        // Search Results
        <div className="divide-y divide-gray-100">
          {searchResults.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">No messages found matching "{searchQuery}"</p>
            </div>
          ) : (
            searchResults.map((result) => (
              <Link
                key={result.id}
                to={`/messages/${result.id}`}
                className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary-600 ph-no-capture">
                    {(result.name || 'C')[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900 truncate ph-no-capture">
                      {result.name || 'Conversation'}
                    </p>
                    <span className="text-xs text-gray-400">
                      {new Date(result.matchingMessage.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate mt-0.5 ph-no-capture">
                    {result.matchingMessage.message}
                  </p>
                </div>
              </Link>
            ))
          )}
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {conversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              currentUserId={user.id}
            />
          ))}
        </div>
      )}
    </div>
  )
}
